import {
  ABILITY_INFO, ABILITY_KINDS, ALL_PIECES, DIAG, DIRS8, KNIGHT, ORTHO, defaultAbility,
  type AbilityDef, type IconId, type MoveRule, type PieceTypeDef, type Vec,
} from '@ic/engine';
import { MoveDiagram } from '../pages/Learn';
import { settings, useStore } from '../lib/store';
import { Field, Segmented, Stepper, Toggle } from './Controls';
import { Icon } from './Icons';
import { PieceIcon } from './Pieces';

const ICONS: IconId[] = ['king', 'queen', 'rook', 'bishop', 'knight', 'pawn', 'dragon', 'bomb', 'miner'];
const QUICK: [string, Vec[]][] = [
  ['Orthogonal', ORTHO], ['Diagonal', DIAG], ['All 8', DIRS8], ['Knight', KNIGHT], ['Forward', [[0, 1]]], ['Clear', []],
];

function VectorGrid({ rule, onChange }: { rule: MoveRule; onChange: (v: Vec[]) => void }) {
  const has = (x: number, y: number) => rule.vectors.some(([a, b]) => a === x && b === y);
  const cells = [];
  for (let y = 3; y >= -3; y--)
    for (let x = -3; x <= 3; x++) {
      const center = x === 0 && y === 0;
      cells.push(
        <button
          key={`${x},${y}`}
          type="button"
          className={`vcell ${center ? 'center' : ''} ${has(x, y) ? 'on' : ''}`}
          disabled={center}
          title={center ? 'piece' : `dx ${x}, dy ${y} (forward = up)`}
          onClick={() => onChange(has(x, y) ? rule.vectors.filter(([a, b]) => a !== x || b !== y) : [...rule.vectors, [x, y]])}
        >{center ? '●' : ''}</button>,
      );
    }
  return <div className="vgrid">{cells}</div>;
}

function RuleCard({ rule, index, onChange, onRemove }: { rule: MoveRule; index: number; onChange: (r: MoveRule) => void; onRemove: () => void }) {
  const set = (patch: Partial<MoveRule>) => onChange({ ...rule, ...patch });
  return (
    <div className="rule-card">
      <div className="rule-head">
        <VectorGrid rule={rule} onChange={(vectors) => set({ vectors })} />
        <div className="stack">
          <span className="section-title">Rule {index + 1}</span>
          <Segmented small value={rule.kind} onChange={(kind) => set({ kind })}
            options={[{ value: 'slide', label: 'Slide', title: 'Repeat the step until blocked' }, { value: 'leap', label: 'Leap', title: 'Jump straight to the square' }]} />
          <div className="quick-dirs">
            {QUICK.map(([label, v]) => <button type="button" key={label} className="btn ghost sm" onClick={() => set({ vectors: v.map((x) => [...x] as Vec) })}>{label}</button>)}
          </div>
        </div>
      </div>
      <div className="rule-opts">
        <Segmented small value={rule.mode} onChange={(mode) => set({ mode })}
          options={[{ value: 'both', label: 'Move + capture' }, { value: 'move', label: 'Move only' }, { value: 'capture', label: 'Capture only' }]} />
        {rule.kind === 'slide' && (
          <Field label="Max distance" hint="0 means unlimited."><Stepper value={rule.max} min={0} max={16} onChange={(max) => set({ max })} /></Field>
        )}
        {rule.kind === 'slide' && <Toggle checked={!!rule.jump} onChange={(jump) => set({ jump })} label="Flies over pieces" />}
        <Toggle checked={!!rule.firstMoveOnly} onChange={(firstMoveOnly) => set({ firstMoveOnly })} label="First move only" />
        <Toggle checked={!!rule.createsEnPassant} onChange={(createsEnPassant) => set({ createsEnPassant })} label="Can be taken en passant" />
        <Toggle checked={!!rule.enPassant} onChange={(enPassant) => set({ enPassant })} label="Captures en passant" />
        <div><button type="button" className="btn danger sm" onClick={onRemove}><Icon name="x" size={14} /> Remove rule</button></div>
      </div>
    </div>
  );
}

function AbilityRow({ kind, def, onChange }: { kind: AbilityDef['kind']; def: PieceTypeDef; onChange: (abilities: AbilityDef[]) => void }) {
  const current = def.abilities.find((a) => a.kind === kind);
  const info = ABILITY_INFO[kind];
  const set = (patch: Partial<AbilityDef>) => onChange(def.abilities.map((a) => (a.kind === kind ? { ...a, ...patch } : a)));
  const enabled = !!current?.enabled;
  return (
    <div className={`ability-row ${enabled ? 'on' : ''}`}>
      <Toggle
        checked={enabled}
        onChange={(v) => { if (!current) onChange([...def.abilities, defaultAbility(kind)]); else set({ enabled: v }); }}
        label={<span className="row gap"><Icon name={kind} size={16} /> {info.name}</span>}
        hint={info.desc}
      />
      {enabled && current && (
        <div className="ability-params">
          <Field label="Cooldown"><Stepper value={current.cooldown} min={0} max={20} suffix="turns" onChange={(cooldown) => set({ cooldown })} /></Field>
          <Field label={kind === 'detonate' ? 'Radius' : kind === 'tunnel' ? 'Distance' : 'Range'}><Stepper value={current.range} min={1} max={8} onChange={(range) => set({ range })} /></Field>
          {(kind === 'timedFuse' || kind === 'dig') && (
            <Field label={kind === 'dig' ? 'Hole lasts' : 'Fuse'}><Stepper value={current.duration ?? 2} min={1} max={10} suffix="turns" onChange={(duration) => set({ duration })} /></Field>
          )}
          {(kind === 'placeMine' || kind === 'plantHedge' || kind === 'multiply') && (
            <Field label={kind === 'placeMine' ? 'Max mines' : kind === 'plantHedge' ? 'Max hedges' : 'Max young alive'}>
              <Stepper value={current.max ?? 2} min={1} max={10} onChange={(max) => set({ max })} />
            </Field>
          )}
          {kind === 'fireBreath' && <Toggle checked={!!current.exhausts} onChange={(exhausts) => set({ exhausts })} label="Exhausts the dragon" />}
        </div>
      )}
    </div>
  );
}

export function PieceEditor({ def, onChange, allTypes }: { def: PieceTypeDef; onChange: (d: PieceTypeDef) => void; allTypes: PieceTypeDef[] }) {
  const { pieceSet } = useStore(settings);
  const set = (patch: Partial<PieceTypeDef>) => onChange({ ...def, ...patch });
  const setRule = (i: number, r: MoveRule) => set({ moves: def.moves.map((m, j) => (j === i ? r : m)) });
  const sources = [...new Map([...Object.values(ALL_PIECES), ...allTypes].map((t) => [t.id, t])).values()].filter((t) => t.id !== def.id);
  return (
    <div className="piece-editor">
      <div className="pe-top">
        <MoveDiagram def={def} />
        <div className="stack">
          <div className="row gap">
            <input value={def.name} onChange={(e) => set({ name: e.target.value })} maxLength={24} aria-label="Name" className="grow" />
            <input value={def.letter} onChange={(e) => set({ letter: e.target.value.toUpperCase().slice(0, 1) })} maxLength={1} className="num" aria-label="Letter" title="Notation letter" />
          </div>
          <div className="icon-picker" role="radiogroup" aria-label="Look">
            {ICONS.map((i) => (
              <button key={i} type="button" role="radio" aria-checked={def.icon === i} className={def.icon === i ? 'on' : ''} title={i} onClick={() => set({ icon: i })}>
                <PieceIcon icon={i} color="w" set={pieceSet} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <h3 className="section-title">Basics</h3>
      <div className="section-card">
        <Field label="Value" hint="How much the bot thinks it's worth."><Stepper value={def.value} min={0} max={50} onChange={(value) => set({ value })} /></Field>
        <Toggle checked={!!def.royal} onChange={(royal) => set({ royal })} label="Royal" hint="Acts as a king: check and king-capture rules apply." />
        <Toggle checked={!!def.castles} onChange={(castles) => set({ castles })} label="Castles" />
        <Toggle checked={!!def.castlePartner} onChange={(castlePartner) => set({ castlePartner })} label="Castling partner" hint="Like a rook." />
        <Toggle checked={!!def.promotes} onChange={(promotes) => set({ promotes })} label="Promotes" hint="Like a pawn reaching the last rank." />
        <Field label="Mine detection" hint="Reveals enemy mines this close."><Stepper value={def.detectRadius ?? 0} min={0} max={8} onChange={(detectRadius) => set({ detectRadius })} /></Field>
      </div>

      <h3 className="section-title">Movement</h3>
      <div className="stack small">
        <span className="muted">Copy the movement of:</span>
        <div className="chips">
          {sources.map((t) => (
            <button type="button" key={t.id} className="chip" onClick={() => set({ moves: JSON.parse(JSON.stringify(t.moves)) })}>
              <span className="chip-icon"><PieceIcon icon={t.icon} color="w" set={pieceSet} /></span>{t.name}
            </button>
          ))}
        </div>
        <span className="muted">Or build it: each rule is a set of directions. Click cells to toggle them; up is “forward” (mirrored for Black).</span>
      </div>
      {def.moves.map((r, i) => (
        <RuleCard key={i} index={i} rule={r} onChange={(nr) => setRule(i, nr)} onRemove={() => set({ moves: def.moves.filter((_, j) => j !== i) })} />
      ))}
      <button type="button" className="btn secondary" onClick={() => set({ moves: [...def.moves, { kind: 'slide', vectors: [[0, 1]], max: 1, mode: 'both' }] })}>
        <Icon name="plus" size={16} /> Add movement rule
      </button>

      <h3 className="section-title">Abilities</h3>
      <div className="ability-grid">
        {ABILITY_KINDS.map((k) => <AbilityRow key={k} kind={k} def={def} onChange={(abilities) => set({ abilities })} />)}
      </div>
    </div>
  );
}
